import React, { useState, useRef, useEffect } from "react";

type TimePickerProps = {
  value?: string | null; // "10:30 PM" or "22:30" etc. Optional
  onChange?: (time: string) => void; // called when user confirms (OK)
  immediate?: boolean; // optional: if true, call onChange every selection (not just OK)
};

export default function TimePicker({ value, onChange, immediate = false }: TimePickerProps) {
  const [open, setOpen] = useState(false);

  // Parse a value like "10:30 PM" or "22:30" safely
  const parseValue = (v?: string | null) => {
    if (!v || typeof v !== "string") return { hour: "", minute: "", ampm: "" };

    // Accept "HH:MM AM/PM" or "HH:MM" (24h)
    const parts = v.trim().split(" ");
    const time = parts[0] ?? "";
    const ap = (parts[1] ?? "").toUpperCase();

    const [hh = "", mm = ""] = time.split(":");

    // If 24h (no AM/PM) convert to 12-hour + AM/PM
    if (!ap) {
      const hnum = parseInt(hh, 10);
      if (!Number.isFinite(hnum)) return { hour: "", minute: "", ampm: "" };
      const ampmCalc = hnum >= 12 ? "PM" : "AM";
      let hour12 = hnum % 12;
      if (hour12 === 0) hour12 = 12;
      return {
        hour: String(hour12).padStart(2, "0"),
        minute: String(mm || "00").padStart(2, "0"),
        ampm: ampmCalc,
      };
    }

    return {
      hour: String(parseInt(hh, 10) || "").padStart?.(2, "0") || "",
      minute: (mm || "00").padStart(2, "0"),
      ampm: (ap === "AM" || ap === "PM") ? ap : "",
    };
  };

  

  // controlled sync: whenever value prop changes update internal state
  useEffect(() => {
    const { hour: h, minute: m, ampm: a } = parseValue(value ?? null);
    setHour(h);
    setMinute(m);
    setAmPm(a);
  }, [value]);

  // internal state
  const [hour, setHour] = useState<string>("");
  const [minute, setMinute] = useState<string>("");
  const [ampm, setAmPm] = useState<string>("");

  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handler = (e: any) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        if (hour && minute && ampm && onChange) {
          onChange(buildOutput());
        }
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [hour, minute, ampm, onChange]);  
  

  const hours = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
  const minutes = ["00","01","02","03","04","05","06","07","08","09",
    "10","11","12","13","14","15","16","17","18","19",
    "20","21","22","23","24","25","26","27","28","29",
    "30","31","32","33","34","35","36","37","38","39",
    "40","41","42","43","44","45","46","47","48","49",
    "50","51","52","53","54","55","56","57","58","59"]
  const ampmList = ["AM", "PM"];

  const okDisabled = !(hour && minute && ampm);

  const buildOutput = () => `${hour.padStart(2, "0")}:${minute.padStart(2, "0")} ${ampm}`;

  // call onChange immediately if immediate=true
  useEffect(() => {
    if (immediate && okDisabled === false && typeof onChange === "function") {
      onChange(buildOutput());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hour, minute, ampm, immediate]);

  const saveValue = () => {
    if (!okDisabled && typeof onChange === "function") {
      onChange(buildOutput());
    }
    setOpen(false);
  };

  // Display label: if nothing selected show placeholder
  const displayLabel = hour && minute && ampm ? buildOutput() : "Select time";

  return (
    <div className="relative w-full" ref={containerRef}>
      {/* Input Box */}
      <div
        className="w-full bg-dark-background-secondary-medium text-white px-4 py-3 rounded-xl cursor-pointer flex items-center justify-between"
        onClick={() => setOpen((s) => !s)}
        role="button"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className={`${hour && minute && ampm ? "text-white" : "text-dark-text"}`}>
          {displayLabel}
        </span>

        {/* Clock Icon */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-6 h-6 text-dark-text"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>

      {/* Popover Wheel Picker */}
      {open && (
        <div className="absolute z-50 mt-2 w-full bg-dark-background-secondary rounded-xl shadow-xl border border-dark-border p-3">
          <div className="flex items-center justify-between">
            {/* Hour Wheel */}
            <div className="flex-1 text-center border-r border-dark-border">
              <div className="h-40 overflow-y-scroll snap-y snap-mandatory no-scrollbar">
                {hours.map((h) => (
                  <div
                    key={h}
                    className={`snap-center py-2 text-lg cursor-pointer ${hour === h ? "text-brand-500 font-semibold" : "text-white"}`}
                    onClick={() => setHour(h)}
                    role="button"
                    tabIndex={0}
                  >
                    {h}
                  </div>
                ))}
              </div>
            </div>

            {/* Minute Wheel */}
            <div className="flex-1 text-center border-r border-dark-border">
              <div className="h-40 overflow-y-scroll snap-y snap-mandatory no-scrollbar">
                {minutes.map((m) => (
                  <div
                    key={m}
                    className={`snap-center py-2 text-lg cursor-pointer ${minute === m ? "text-brand-500 font-semibold" : "text-white"}`}
                    onClick={() => setMinute(m)}
                    role="button"
                    tabIndex={0}
                  >
                    {m}
                  </div>
                ))}
              </div>
            </div>

            {/* AM/PM Wheel */}
            <div className="flex-1 text-center">
              <div className="h-40 overflow-y-scroll snap-y snap-mandatory no-scrollbar">
                {ampmList.map((a) => (
                  <div
                    key={a}
                    className={`snap-center py-2 text-lg cursor-pointer ${ampm === a ? "text-brand-500 font-semibold" : "text-white"}`}
                    onClick={() => setAmPm(a)}
                    role="button"
                    tabIndex={0}
                  >
                    {a}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="mt-3 flex justify-between px-2">
            <button onClick={() => setOpen(false)} className="text-dark-text">
              CANCEL
            </button>

            <button
              onClick={saveValue}
              className={`font-semibold ${okDisabled ? "text-gray-500 pointer-events-none" : "text-brand-500"}`}
              disabled={okDisabled}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
